import { QueryString } from 'core/utils/queryString/queryString';
import { FilterCode } from 'gql/autogen/FilterCode';
import {
    BookingFeatureQueryToValues,
    BookingFeatureKeyToQueryKey,
    CategoryQueryKeyToApiId,
    DurationKeyToQueryKey,
    DurationQueryToValues,
    SortCodeToQuery,
    SortQueryToCode,
    SubcategoryApiIdToQueryKey,
    SubcategoryQueryKeyToApiId,
} from 'component/page/search/SortAndFilter/SortAndFilterQuery.types';
import { getRangeEntryLowerBoundValue, getRangeEntryUpperBoundValue, getRangeFilterEntry, getValueFilterEntry } from 'component/page/search/SortAndFilter/Filter/filter.utils';
import { SortField } from 'component/page/search/SortAndFilter/Sort/SortCard.types';
import { getSortCodeOrder } from 'component/page/search/SortAndFilter/Sort/sort.utils';
import { RangeFilterEntry } from 'gql/autogen/RangeFilterEntry';
import { ValueFilterEntry } from 'gql/autogen/ValueFilterEntry';
import { parseNumber } from 'core/utils/parseNumber';
import { SearchQueryKey } from 'core-framework/constant/SearchQueryKey';
import { isOn } from 'core/window/bootstrapConfig';
import { getBootstrapConfig } from 'core/window/windowService';
import { SortCode } from 'gql/autogen/SortCode';
import { SortOrder } from 'gql/autogen/SortOrder';
import { Sort } from 'gql/autogen/Sort';
import { FeatureName } from 'core/constant/FeatureName';
import { CategoryApiIdToQueryKey } from 'core/type/Category.types';

interface FilterState {
    rangeFilterMatrix: RangeFilterEntry[];
    valueFilterMatrix: ValueFilterEntry[];
}

const updateSort = (query: QueryString, sortState: Sort) => {
    const sortCodeOrder = getSortCodeOrder(sortState);
    if (!sortState.code || sortCodeOrder === SortField.DEFAULT_DEFAULT) {
        query.delete(SearchQueryKey.Sort);
    } else {
        query.set(SearchQueryKey.Sort, SortCodeToQuery[sortCodeOrder]);
    }
};

const updateValueFilterInQuery = (query: QueryString, filterCode: FilterCode, filterState: FilterState, keyMapping: Record<string, string>, queryKey: SearchQueryKey) => {
    const filter = getValueFilterEntry(filterCode, filterState.valueFilterMatrix);

    if (filter) {
        const values = filter.values
            .filter((v) => !!keyMapping[v])
            .map((key) => keyMapping[key])
            .join(',');
        query.set(queryKey, values);
    } else {
        query.delete(queryKey);
    }
};

const updatePriceFilterInQuery = (query: QueryString, filterCode: FilterCode, filterState: FilterState) => {
    const filter = getRangeFilterEntry(filterCode, filterState.rangeFilterMatrix);

    if (filter?.ranges?.length) {
        const priceRange = filter.ranges[0];
        query.set(SearchQueryKey.PriceFrom, String(getRangeEntryLowerBoundValue(priceRange)));
        query.set(SearchQueryKey.PriceTo, String(getRangeEntryUpperBoundValue(priceRange)));
    } else {
        query.delete(SearchQueryKey.PriceFrom);
        query.delete(SearchQueryKey.PriceTo);
    }
};

const updateStarRatingFilterInQuery = (query: QueryString, filterCode: FilterCode, filterState: FilterState) => {
    const filter = getRangeFilterEntry(filterCode, filterState.rangeFilterMatrix);

    if (filter?.ranges?.length) {
        const values = filter.ranges.map((r) => getRangeEntryLowerBoundValue(r)).join(',');
        query.set(SearchQueryKey.Rating, values);
    } else {
        query.delete(SearchQueryKey.Rating);
    }
};

const updateCategoryFilterInQuery = (query: QueryString, filterCode: FilterCode, filterState: FilterState) => {
    const filter = getValueFilterEntry(filterCode, filterState.valueFilterMatrix);

    if (filter) {
        const values = filter.values
            .filter((key) => !!CategoryApiIdToQueryKey[key])
            .map((key) => CategoryApiIdToQueryKey[key])
            .join(',');
        query.set(SearchQueryKey.Category, values);
    } else {
        query.delete(SearchQueryKey.Category);
    }
};

const updateSubcategoryFilterInQuery = (query: QueryString, filterCode: FilterCode, filterState: FilterState) => {
    const filter = getValueFilterEntry(filterCode, filterState.valueFilterMatrix);

    if (filter) {
        const values = filter.values
            .filter((key) => !!SubcategoryApiIdToQueryKey[key])
            .map((key) => SubcategoryApiIdToQueryKey[key])
            .join(',');
        query.set(SearchQueryKey.Subcategory, values);
    } else {
        query.delete(SearchQueryKey.Subcategory);
    }
};

const setPriceFilterFromQuery = (query: QueryString, filterState: FilterState) => {
    const min = parseNumber(query.get(SearchQueryKey.PriceFrom));
    const max = parseNumber(query.get(SearchQueryKey.PriceTo));
    const config = getBootstrapConfig();
    const conditionAct3489A = min >= max;
    const conditionAct3489B = min > max;
    const shouldResetPrice = isOn(FeatureName.LowHighResetPriceFilter, config);
    const enableMinMaxEqual = shouldResetPrice ? conditionAct3489B : conditionAct3489A;

    if (min < 0 || max <= 0 || enableMinMaxEqual) {
        return;
    }

    filterState.rangeFilterMatrix.push({
        code: FilterCode.Price,
        ranges: [
            {
                lowerBound: { isInclusive: true, value: Math.max(0, min) },
                upperBound: { isInclusive: true, value: Math.min(max, Number.MAX_SAFE_INTEGER) },
            },
        ],
    });
};

const setStarRatingFilterFromQuery = (query: QueryString, filterState: FilterState) => {
    const rawValues = query.get(SearchQueryKey.Rating);
    const values = rawValues
        ? rawValues
              .split(',')
              .filter((v) => v !== '' && !Number.isNaN(Number(v)))
              .map((v) => Number(v))
              .filter((f) => f >= 0 && f <= 5)
        : [];
    if (values.length) {
        const filter: RangeFilterEntry = {
            code: FilterCode.Rating,
            ranges: [],
        };
        values.forEach((f) => {
            filter.ranges.push({
                lowerBound: { isInclusive: true, value: f },
                upperBound: { isInclusive: false, value: f + 1 },
            });
        });
        filterState.rangeFilterMatrix.push(filter);
    }
};

const setDurationFilterFromQuery = (query: QueryString, filterState: FilterState) => {
    const rawValues = query.get(SearchQueryKey.Duration);
    const values: string[] = rawValues
        ? rawValues
              .split(',')
              .filter((v) => v !== '' && DurationQueryToValues[v])
              .reduce<string[]>((prev, current) => {
                  return [...prev, ...DurationQueryToValues[current]];
              }, [])
        : [];

    if (values.length) {
        filterState.valueFilterMatrix.push({
            values,
            code: FilterCode.Duration,
        });
    }
};

const setBookingfeatureFilterFromQuery = (query: QueryString, filterState: FilterState) => {
    const rawValues = query.get(SearchQueryKey.BookingFeature);
    const values: string[] = rawValues
        ? rawValues
              .split(',')
              .filter((v) => v !== '' && BookingFeatureQueryToValues[v])
              .reduce<string[]>((prev, current) => {
                  return [...prev, ...BookingFeatureQueryToValues[current]];
              }, [])
        : [];

    if (values.length) {
        filterState.valueFilterMatrix.push({
            values,
            code: FilterCode.BookingFeature,
        });
    }
};

const setSupplierFilterFromQuery = (query: QueryString, filterState: FilterState) => {
    const rawValues = query.get(SearchQueryKey.Supplier) || '';
    if (!rawValues || rawValues.length === 0) return;

    const values: string[] = rawValues
        .split(',')
        .map((supplierValueOrEmpty) => {
            const supplierValue = (supplierValueOrEmpty || '').trim();
            return supplierValue;
        })
        .filter((s) => s)
        .map((s) => s.toLowerCase());

    if (values.length > 0) {
        filterState.valueFilterMatrix.push({ values, code: FilterCode.Supplier });
    }
};

const setCategoryFilterFromQuery = (query: QueryString, filterState: FilterState) => {
    const config = getBootstrapConfig();
    const isSubcategoryEnabled = isOn(FeatureName.SubcategoryFilter, config);
    const rawValues = query.get(SearchQueryKey.Category);
    const values: string[] = rawValues
        ? rawValues
              .split(',')
              .filter((v) => v !== '' && CategoryQueryKeyToApiId[v])
              .map((v) => CategoryQueryKeyToApiId[v])
        : [];

    if (values.length) {
        if (isSubcategoryEnabled) {
            filterState.valueFilterMatrix.push({
                values,
                code: FilterCode.Category,
            });
        } else {
            // use only first value as tab will only support one category at a time
            const firstValue = values[0];
            filterState.valueFilterMatrix.push({
                values: [firstValue],
                code: FilterCode.Category,
            });
        }
    }
};

const setSubcategoryFilterFromQuery = (query: QueryString, filterState: FilterState) => {
    const config = getBootstrapConfig();
    const isSubcategoryEnabled = isOn(FeatureName.SubcategoryFilter, config);
    if (!isSubcategoryEnabled) return;

    const rawValues = query.get(SearchQueryKey.Subcategory);
    const values: string[] = rawValues
        ? rawValues
              .split(',')
              .filter((v) => v !== '' && SubcategoryQueryKeyToApiId[v])
              .map((v) => SubcategoryQueryKeyToApiId[v])
        : [];

    if (values.length) {
        filterState.valueFilterMatrix.push({
            values,
            code: FilterCode.SubCategory,
        });
    }
};

export const getSortAndFilterQueryString = (locationSearch: string, sortState: Sort, filterState: FilterState, deletePagination = false, deleteKeyword = false): string => {
    const query = new QueryString(locationSearch);
    updateSort(query, sortState);
    updateValueFilterInQuery(query, FilterCode.Duration, filterState, DurationKeyToQueryKey, SearchQueryKey.Duration);
    updateValueFilterInQuery(query, FilterCode.BookingFeature, filterState, BookingFeatureKeyToQueryKey, SearchQueryKey.BookingFeature);
    updatePriceFilterInQuery(query, FilterCode.Price, filterState);
    updateStarRatingFilterInQuery(query, FilterCode.Rating, filterState);
    updateCategoryFilterInQuery(query, FilterCode.Category, filterState);
    updateSubcategoryFilterInQuery(query, FilterCode.SubCategory, filterState);
    if (deletePagination) {
        query.delete(SearchQueryKey.PageNumber);
    }
    if (deleteKeyword) {
        query.delete(SearchQueryKey.Keyword);
    }
    return query.toString();
};

export const updateCategoryQueryString = (locationSearch: string, locationHash: string, filterState: FilterState): string => {
    const query = new QueryString(locationSearch);
    updateCategoryFilterInQuery(query, FilterCode.Category, filterState);
    return query.toString() + locationHash;
};

const DefaultSort: Sort = { code: SortCode.Default, order: SortOrder.Default };
export const getSortStateFromQueryParams = (locationSearch: string): Sort => {
    const query = new QueryString(locationSearch);
    const sort = query.get(SearchQueryKey.Sort) || '';
    return { ...DefaultSort, ...SortQueryToCode[sort] };
};

export const getFilterStateFromQueryParams = (locationSearch: string): FilterState => {
    const query = new QueryString(locationSearch);
    const filterState: FilterState = { rangeFilterMatrix: [], valueFilterMatrix: [] };

    setPriceFilterFromQuery(query, filterState);
    setStarRatingFilterFromQuery(query, filterState);
    setDurationFilterFromQuery(query, filterState);
    setSupplierFilterFromQuery(query, filterState);
    setCategoryFilterFromQuery(query, filterState);
    setSubcategoryFilterFromQuery(query, filterState);
    setBookingfeatureFilterFromQuery(query, filterState);

    return filterState;
};
